import { apiGetErrorStatus } from "@raiden/library-ui/helpers/api";
import { serverApiFetcher } from "@raiden/library-ui/hooks/useApiFetcher";
import generateApiUrl from "@raiden/library-ui/libraries/utils/generateApiUrl";
import { getHotelSlug } from "@raiden/library-ui/helpers/hotels/getSlug";
import { PageErrorHandlerPublic } from "../../containers/PageErrorHandlerPublic";
import { frontBaseUri } from "@raiden/library-ui/constants/routers/front";
import { Box } from "@chakra-ui/react";
import { getNumber } from "@raiden/library-ui/hooks/router/useRouterNumber";
import generateFrontUrl from "@raiden/library-ui/libraries/utils/generateFrontUrl";

// #region getUrlRoom
/**
 * @param {object} params
 * @param {number} params.roomId
 */
function getUrlRoom({ roomId }) {
  return generateApiUrl({
    id: "@rooms.view",
    parameters: {
      roomId,
    },
    query: {
      fields: ["rooms.hotel"],
    },
  });
}

// #region getStatus
/**
 * Resolves the status code of the page based on the response and error.
 * @param {object} params
 * @param {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Room").Room>} [params.response]
 * @param {import("@raiden/library-ui/types/Api/ApiError").ApiError} [params.error]
 * @returns {number | undefined}
 */
function getStatus({ response, error }) {
  if (response) {
    return 200;
  }
  if (error) {
    return apiGetErrorStatus({ error: error });
  }
  return undefined;
}

// #region component
/**
 * @typedef {object} Props
 * @property {number} [serverStatus]
 */
/**
 * @param {Props} props
 */
function Page({ serverStatus }) {
  const status = serverStatus;

  return (
    <PageErrorHandlerPublic status={status}>
      <Box>Erreur</Box>
    </PageErrorHandlerPublic>
  );
}

export default Page;

// #region getInitialProps
/**
 * @typedef {import("../../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  const { res, locale } = context;
  /** @type {InitialProps} */
  const values = {
    layout: "public",
    serverStatus: 404,
  };
  const DEFAULT_LOCALE = process.env.NEXT_PUBLIC_DEFAULT_LOCALE;

  if (typeof window === "undefined") {
    const roomId = getNumber(context.query.roomSlug);

    if (roomId !== undefined) {
      let roomError = undefined;

      /** @type {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Room").Room> | undefined} */
      const roomResponse = await serverApiFetcher(
        getUrlRoom({ roomId }),
        undefined,
        {
          origin: frontBaseUri.toString(),
          cookies: context.cookies,
        },
      ).catch(
        (
          /** @type {import("@raiden/library-ui/types/Api/ApiError").ApiError} */ error,
        ) => {
          roomError = error;
          return undefined;
        },
      );

      const serverStatus = getStatus({
        response: roomResponse,
        error: roomError,
      });

      if (serverStatus === 200 && res && roomResponse?.data?.hotel) {
        // Redirection côté serveur
        res.writeHead(301, {
          Location: generateFrontUrl({
            id: "@front.hotels.view",
            includeBasePath: true,
            parameters: {
              locale,
              hotelSlug: getHotelSlug({
                hotel: roomResponse?.data?.hotel,
                locale,
                defaultLocale: DEFAULT_LOCALE,
              }),
            },
          }),
        });
        res.end();
      }
    }
  }

  return values;
};
